export const ROVO_LINK_PICKER_WIDTH_IN_PX = 464;

/**
 * Total height of the RovoLinkPicker component in pixels.
 *
 * Note: This value is duplicated in editor-common/src/ui/LinkSearch/const.ts
 * for use by the editor's floating toolbar popup placement logic.
 * If you change this value, update that file too.
 */
export const ROVO_LINK_PICKER_HEIGHT_IN_PX = 512;

/**
 * Minimum height for the content area below the tabs/input.
 * Keeps the picker height stable across state transitions (landing → loading → results)
 * to prevent layout content shift. The value matches the tallest intrinsic state
 * (LandingState with illustration, heading, and subtitle).
 *
 * Note: This value is duplicated as a string literal ('340px') in SearchResultsSection
 * and RovoLinkPicker cssMap definitions because cssMap does not allow variable interpolation.
 * If you change this value, update both files.
 */
export const CONTENT_AREA_MIN_HEIGHT = '340px';

export const AGG_ABSOLUTE_URL = '/gateway/api/graphql';

export const EXPERIENCE_NAME = 'rovo-link-picker';

export const CONFLUENCE_ARI = 'ari:cloud:confluence::site';

export const SESSION_ID = 'link-picker-session';

export const ANALYTICS_SOURCE = 'rovoLinkPicker';

/**
 * UFO (Unified Frontend Observability) interaction names used with
 * `@atlaskit/react-ufo` press/interaction tracing. These mirror the structure
 * used by Quick Find (see `search-page` `common/constants/quick-find`) so that
 * Rovo Link Picker press interactions are attributed to a consistent, named
 * interaction in UFO.
 */

/** Press interaction fired when the Rovo Link Picker is opened. */
export const ROVO_LINK_PICKER_OPEN_INTERACTION = 'rovo-link-picker-open';

/**
 * Press interaction fired when the Rovo Link Picker is closed. Traced (at most once per picker
 * lifetime) on submit/select, and on unmount to cover dismissal paths that don't run
 * submit/select — e.g. Escape, cancel, outside-click, or the parent unmounting the picker.
 */
export const ROVO_LINK_PICKER_CLOSE_INTERACTION = 'rovo-link-picker-close';

/**
 * Press interaction fired when pre-query (recents) results are requested — i.e. on
 * open and on tab switch / query-cleared transitions back to recents. Bounded by the
 * loading hold below, this attributes "pre-query results shown" to a named, timed UFO
 * interaction (mirrors Quick Find's landing/pre-query instrumentation).
 */
export const ROVO_LINK_PICKER_PRE_QUERY_INTERACTION = 'rovo-link-picker-pre-query';

/** Press interaction fired when a search (post-query) is triggered by the user. */
export const ROVO_LINK_PICKER_POST_QUERY_INTERACTION = 'rovo-link-picker-post-query';

/**
 * Search debounce window (ms). This is the single source of truth for how long we wait after the
 * last keystroke before firing a search request (see `useSearch`'s `debounceMs` default).
 *
 * It is also used to back-date the post-query UFO press interaction start time: the keystroke that
 * triggers the debounced search is the true start of the interaction, so we offset the press start
 * by this window (mirrors Quick Find's post-query press instrumentation, QS-7380 —
 * https://hello.atlassian.net/wiki/x/QVo8QwE). Keep this value and `useSearch`'s default in sync.
 */
export const ROVO_LINK_PICKER_SEARCH_DEBOUNCE_MS = 300;

/**
 * UFO load-hold name rendered while config/results are loading. Mounting this hold keeps
 * the active interaction (open / pre-query / post-query) open until results render; its
 * release defines the "results shown" end time, which powers reliability + latency
 * signals downstream (mirrors Quick Find's `QUICK_FIND_LOADING_HOLD`).
 */
export const ROVO_LINK_PICKER_LOADING_HOLD = 'rovo-link-picker-loading';

/** UFO segment name wrapping the Rovo Link Picker component tree. */
export const ROVO_LINK_PICKER_UFO_SEGMENT_NAME = 'rovo-link-picker';
